import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { type FindingSeverity, type AuditCycle, type CompanyProfile } from "@/hooks/use-audit-findings";

interface RaiseFindingModalProps {
  open: boolean;
  onClose: () => void;
  relatedItem: string;
  profiles: CompanyProfile[];
  cycles: AuditCycle[];
  onSubmit: (severity: FindingSeverity, description: string, assignedTo: string | null, cycleId: string | null) => void;
}

const severityConfig: Record<FindingSeverity, { label: string; color: string }> = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800 border-red-200' },
  high:     { label: 'High',     color: 'bg-orange-100 text-orange-800 border-orange-200' },
  medium:   { label: 'Medium',   color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  low:      { label: 'Low',      color: 'bg-blue-100 text-blue-800 border-blue-200' },
};

export function RaiseFindingModal({ open, onClose, relatedItem, profiles, cycles, onSubmit }: RaiseFindingModalProps) {
  const [severity, setSeverity] = useState<FindingSeverity>('medium');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState<string>('');
  const [cycleId, setCycleId] = useState<string>('');

  const handleSubmit = () => {
    if (!description.trim()) return;
    onSubmit(severity, description.trim(), assignedTo || null, cycleId || null);
    reset();
    onClose();
  };

  const reset = () => {
    setDescription('');
    setSeverity('medium');
    setAssignedTo('');
    setCycleId('');
  };

  const handleClose = () => { reset(); onClose(); };

  const activeCycles = cycles.filter((c) => c.status === 'active');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Raise Audit Finding
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-sm text-muted-foreground">Related Item</Label>
            <div className="px-3 py-2 rounded-md border bg-muted/40 text-sm font-medium">{relatedItem}</div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="severity">Severity</Label>
            <Select value={severity} onValueChange={(v) => setSeverity(v as FindingSeverity)}>
              <SelectTrigger id="severity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(severityConfig) as FindingSeverity[]).map((s) => (
                  <SelectItem key={s} value={s}>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${severityConfig[s].color}`} variant="outline">
                        {severityConfig[s].label}
                      </Badge>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe the finding — what was observed, what control failed, and the potential impact..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="assigned-to">Assign To</Label>
              <Select value={assignedTo} onValueChange={setAssignedTo}>
                <SelectTrigger id="assigned-to">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {profiles.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="cycle">Audit Cycle</Label>
              <Select value={cycleId} onValueChange={setCycleId}>
                <SelectTrigger id="cycle">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {activeCycles.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                  {activeCycles.length === 0 && (
                    <SelectItem value="no-cycles" disabled>No active cycles</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!description.trim()}>Raise Finding</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
